import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  FaArrowLeft, FaUserCircle, FaMapMarkerAlt, FaPhoneAlt, 
  FaSeedling, FaRulerCombined, FaTint, FaUserClock, 
  FaGlobe, FaBell, FaRuler, FaSignOutAlt, FaEdit, FaChevronRight 
} from 'react-icons/fa';
import './FarmerProfile.css';

const FarmerProfile = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const currentLang = i18n.language || 'hi';
  const isHindi = currentLang === 'hi';

  const [profile, setProfile] = useState(() => {
    const saved = localStorage.getItem('farmerProfile');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {}
    }
    return {
      name: 'Ramesh Yadav',
      phone: '9876543210',
      location: 'Indore, Madhya Pradesh',
      landSize: '5 Acres',
      mainCrop: 'Wheat, Soybean',
      waterSource: 'Borewell',
      experience: '12 Years',
      notifications: true,
      units: 'Metric (°C, mm)'
    };
  });

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editForm, setEditForm] = useState({ ...profile });
  const [saveSuccess, setSaveSuccess] = useState(false);

  const handleEditClick = () => {
    setEditForm({ ...profile });
    setIsEditModalOpen(true);
  };

  const handleFormChange = (field, value) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setEditForm(prev => ({ ...prev, photoURL: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setProfile(editForm);
    localStorage.setItem('farmerProfile', JSON.stringify(editForm));
    
    // Sync to MongoDB Atlas
    try {
      await fetch('http://127.0.0.1:8000/api/farmer/profile', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editForm)
      });
    } catch (err) {
      console.warn('MongoDB profile sync notice:', err);
    }

    setSaveSuccess(true);
    setTimeout(() => {
      setSaveSuccess(false);
      setIsEditModalOpen(false);
    }, 800);
  };

  const handleLanguageChange = (e) => {
    const newLang = e.target.value;
    i18n.changeLanguage(newLang);
    localStorage.setItem('i18nextLng', newLang);
  };

  const handleLogout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('farmerProfile');
    localStorage.removeItem('is_authenticated');
    localStorage.removeItem('is_fd_admin');
    localStorage.removeItem('is_fd_super_admin');
    window.dispatchEvent(new Event('storage'));
    window.dispatchEvent(new CustomEvent('auth-change', { detail: { authenticated: false } }));
    navigate('/login');
  };

  return (
    <div className="fd-profile-page">
      <div className="fd-profile-container">
        {/* Top Header */}
        <div className="fd-profile-header-row">
          <Link to="/dashboard" className="fd-prof-back-btn">
            <FaArrowLeft />
            <span>{isHindi ? 'वापस' : 'Back'}</span>
          </Link>
          <h2>{isHindi ? 'मेरा प्रोफ़ाइल और सेटिंग्स' : 'Profile & Settings'}</h2>
        </div>

        {/* Farmer Header Card */}
        <div className="fd-farmer-head-card fd-card">
          <div className="fd-farmer-head-avatar">
            {profile.photoURL ? (
              <img src={profile.photoURL} alt={profile.name} className="fd-farmer-avatar-img" />
            ) : (
              <FaUserCircle />
            )}
          </div>
          <div className="fd-farmer-head-details">
            <h3>{profile.name}</h3>
            <div className="fd-fhd-meta">
              <span><FaMapMarkerAlt className="fd-meta-icon" /> {profile.location}</span>
              <span><FaPhoneAlt className="fd-meta-icon" /> {profile.phone || '9822725265'}</span>
            </div>
          </div>
          <button className="fd-edit-prof-btn" onClick={handleEditClick}>
            <FaEdit />
            <span>{isHindi ? 'संपादित करें' : 'Edit Profile'}</span>
          </button>
        </div>

        {/* Farm Details Group */}
        <div className="fd-profile-section-card fd-card">
          <div className="fd-section-title-line">
            <h3>{isHindi ? 'खेत और फसल विवरण (Farm Details)' : 'Farm Details'}</h3>
          </div>

          <div className="fd-settings-list">
            <div className="fd-setting-item">
              <div className="fd-setting-left">
                <FaSeedling className="fd-set-icon green" />
                <span className="fd-set-label">{isHindi ? 'मुख्य फसलें' : 'Main Crops'}</span>
              </div>
              <div className="fd-setting-right">
                <span className="fd-set-val">{isHindi ? 'गेहूं, सोयाबीन' : profile.mainCrop}</span>
                <FaChevronRight className="fd-set-arr" />
              </div>
            </div>

            <div className="fd-setting-item">
              <div className="fd-setting-left">
                <FaRulerCombined className="fd-set-icon gold" />
                <span className="fd-set-label">{isHindi ? 'भूमि का आकार' : 'Land Size'}</span>
              </div>
              <div className="fd-setting-right">
                <span className="fd-set-val">{isHindi ? '5 एकड़' : profile.landSize}</span>
                <FaChevronRight className="fd-set-arr" />
              </div>
            </div>

            <div className="fd-setting-item">
              <div className="fd-setting-left">
                <FaTint className="fd-set-icon blue" />
                <span className="fd-set-label">{isHindi ? 'पानी का मुख्य स्रोत' : 'Water Source'}</span>
              </div>
              <div className="fd-setting-right">
                <span className="fd-set-val">{isHindi ? 'बोरवेल' : profile.waterSource}</span>
                <FaChevronRight className="fd-set-arr" />
              </div>
            </div>

            <div className="fd-setting-item">
              <div className="fd-setting-left">
                <FaUserClock className="fd-set-icon gray" />
                <span className="fd-set-label">{isHindi ? 'खेती का अनुभव' : 'Farming Experience'}</span>
              </div>
              <div className="fd-setting-right">
                <span className="fd-set-val">{isHindi ? '12 वर्ष' : profile.experience}</span>
                <FaChevronRight className="fd-set-arr" />
              </div>
            </div>
          </div>
        </div>

        {/* Preferences & Settings */}
        <div className="fd-profile-section-card fd-card">
          <div className="fd-section-title-line">
            <h3>{isHindi ? 'प्राथमिकताएं (Preferences)' : 'Preferences & Settings'}</h3>
          </div>

          <div className="fd-settings-list">
            <div className="fd-setting-item">
              <div className="fd-setting-left">
                <FaGlobe className="fd-set-icon green" />
                <span className="fd-set-label">{isHindi ? 'ऐप की भाषा' : 'App Language'}</span>
              </div>
              <div className="fd-setting-right">
                <select value={currentLang} onChange={handleLanguageChange} className="fd-lang-inline-select">
                  <option value="hi">हिंदी (Hindi)</option>
                  <option value="en">English</option>
                  <option value="mr">मराठी (Marathi)</option>
                  <option value="gu">ગુજરાતી (Gujarati)</option>
                </select>
              </div>
            </div>

            <div className="fd-setting-item">
              <div className="fd-setting-left">
                <FaBell className="fd-set-icon gold" />
                <span className="fd-set-label">{isHindi ? 'मौसम व डिस्ट्रेस अलर्ट' : 'Distress & Weather Alerts'}</span>
              </div>
              <div className="fd-setting-right">
                <span className="fd-badge-low">{isHindi ? 'सक्रिय (Enabled)' : 'Enabled'}</span>
                <FaChevronRight className="fd-set-arr" />
              </div>
            </div>

            <div className="fd-setting-item">
              <div className="fd-setting-left">
                <FaRuler className="fd-set-icon blue" />
                <span className="fd-set-label">{isHindi ? 'माप इकाइयां' : 'Units'}</span>
              </div>
              <div className="fd-setting-right">
                <span className="fd-set-val">Metric (°C, mm, kg/ha)</span>
                <FaChevronRight className="fd-set-arr" />
              </div>
            </div>
          </div>
        </div>

        {/* Logout Button */}
        <button className="fd-logout-btn fd-card" onClick={handleLogout}>
          <FaSignOutAlt />
          <span>{isHindi ? 'लॉगआउट करें (Logout)' : 'Logout of Account'}</span>
        </button>
      </div>

      {/* Profile Editing Modal */}
      {isEditModalOpen && (
        <div className="fd-modal-backdrop" onClick={() => setIsEditModalOpen(false)}>
          <div className="fd-edit-modal-card fd-card" onClick={(e) => e.stopPropagation()}>
            <div className="fd-modal-header">
              <h3>{isHindi ? '📝 किसान प्रोफ़ाइल संपादित करें' : '📝 Edit Farmer Profile'}</h3>
              <button className="fd-modal-close-btn" onClick={() => setIsEditModalOpen(false)}>✕</button>
            </div>

            <form onSubmit={handleSaveProfile} className="fd-edit-profile-form">
              {/* Photo Avatar Section */}
              <div className="fd-avatar-edit-row">
                <div className="fd-avatar-preview-box">
                  {editForm.photoURL ? (
                    <img src={editForm.photoURL} alt="Preview" className="fd-edit-avatar-img" />
                  ) : (
                    <FaUserCircle className="fd-edit-avatar-icon" />
                  )}
                </div>
                <div className="fd-avatar-upload-info">
                  <span className="fd-avatar-badge-label">
                    {editForm.photoURL ? '🟢 Google Account Avatar Loaded' : '👤 Default Avatar'}
                  </span>
                  <label className="fd-upload-photo-btn">
                    📷 {isHindi ? 'फ़ोटो बदलें / अपलोड करें' : 'Change / Upload Photo'}
                    <input type="file" accept="image/*" onChange={handlePhotoUpload} style={{ display: 'none' }} />
                  </label>
                </div>
              </div>

              {/* Form Grid */}
              <div className="fd-modal-grid-2col">
                <div className="fd-modal-input-group">
                  <label>{isHindi ? 'किसान का नाम' : 'Farmer Name'}</label>
                  <input 
                    type="text" 
                    value={editForm.name || ''} 
                    onChange={(e) => handleFormChange('name', e.target.value)} 
                    required 
                  />
                </div>

                <div className="fd-modal-input-group">
                  <label>{isHindi ? 'मोबाइल नंबर' : 'Phone / Mobile'}</label>
                  <input 
                    type="tel" 
                    value={editForm.phone || ''} 
                    onChange={(e) => handleFormChange('phone', e.target.value)} 
                    placeholder="9876543210" 
                  />
                </div>

                <div className="fd-modal-input-group">
                  <label>{isHindi ? 'खेत का स्थान / ज़िला' : 'Farm Location / District'}</label>
                  <input 
                    type="text" 
                    value={editForm.location || ''} 
                    onChange={(e) => handleFormChange('location', e.target.value)} 
                  />
                </div>

                <div className="fd-modal-input-group">
                  <label>{isHindi ? 'भूमि का आकार' : 'Land Size'}</label>
                  <input 
                    type="text" 
                    value={editForm.landSize || ''} 
                    onChange={(e) => handleFormChange('landSize', e.target.value)} 
                    placeholder="5 Acres" 
                  />
                </div>

                <div className="fd-modal-input-group">
                  <label>{isHindi ? 'मुख्य फसल' : 'Primary Crop'}</label>
                  <select 
                    value={editForm.mainCrop || 'Banana'} 
                    onChange={(e) => handleFormChange('mainCrop', e.target.value)}
                  >
                    <option value="Banana">Banana (केला / केळी - G9)</option>
                    <option value="Cotton">Cotton (कपास / कापूस)</option>
                    <option value="Soybean">Soybean (सोयाबीन)</option>
                    <option value="Wheat">Wheat (गेहूं / गहू)</option>
                    <option value="Onion">Onion (प्याज / कांदा)</option>
                    <option value="Tomato">Tomato (टमाटर / टोमॅटो)</option>
                    <option value="Grapes">Grapes (अंगूर / द्राक्षे)</option>
                  </select>
                </div>

                <div className="fd-modal-input-group">
                  <label>{isHindi ? 'सिंचाई का साधन' : 'Irrigation / Water Source'}</label>
                  <select 
                    value={editForm.waterSource || 'Drip Irrigation'} 
                    onChange={(e) => handleFormChange('waterSource', e.target.value)}
                  >
                    <option value="Drip Irrigation">Drip Irrigation (ड्रिप / ठिबक)</option>
                    <option value="Borewell">Borewell (बोरवेल)</option>
                    <option value="Canal">Canal (नहर / कालवा)</option>
                    <option value="Rainfed">Rainfed (वर्षा आधारित)</option>
                  </select>
                </div>
              </div>

              {/* Modal Action Buttons */}
              <div className="fd-modal-actions">
                <button 
                  type="button" 
                  className="fd-modal-cancel-btn" 
                  onClick={() => setIsEditModalOpen(false)}
                >
                  {isHindi ? 'रद्द करें' : 'Cancel'}
                </button>
                <button 
                  type="submit" 
                  className="fd-btn-primary fd-modal-save-btn"
                >
                  {saveSuccess ? (isHindi ? '✓ सुरक्षित हो गया!' : '✓ Saved to Cloud!') : (isHindi ? 'परिवर्तन सुरक्षित करें' : 'Save Changes')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FarmerProfile;
